'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { fetchPrV2CodeChanges } from './fetch-pr-v2';
import { getPrV2CacheSource, logPrV2CacheUsage } from './prefetch-debug';
import { prV2CodeChangesQueryKey } from './query-keys';

/** Diffs can be megabytes, and the desktop app preloads ones nobody may open. */
const PR_V2_CODE_CHANGES_GC_TIME_MS = 30 * 60_000;

/**
 * A comparison's code changes, for the PR page and every prefetch of it. Keyed by both SHAs, so
 * the data never goes stale. A query keeps the longest `gcTime` any caller gives it, so a caller
 * left on the client default would keep the diff in memory for good.
 */
export function prV2CodeChangesQueryOptions(
  owner: string,
  repo: string,
  number: string,
  comparison: { headSha?: string | null; baseSha?: string | null },
) {
  return {
    queryKey: prV2CodeChangesQueryKey(owner, repo, number, comparison.headSha, comparison.baseSha),
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchPrV2CodeChanges(owner, repo, number, comparison, signal),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: PR_V2_CODE_CHANGES_GC_TIME_MS,
  };
}

export function usePrV2CodeChanges(
  owner: string,
  repo: string,
  number: string,
  options?: {
    headSha?: string | null;
    baseSha?: string | null;
    enabled?: boolean;
  },
) {
  const queryClient = useQueryClient();
  const codeChangesQuery = prV2CodeChangesQueryOptions(owner, repo, number, {
    headSha: options?.headSha,
    baseSha: options?.baseSha,
  });
  const { queryKey } = codeChangesQuery;
  const queryEnabled = Boolean(owner && repo && number) && (options?.enabled ?? true);
  const preMountSnapshotRef = useRef<{
    queryHash: string;
    hasData: boolean;
    source: ReturnType<typeof getPrV2CacheSource>;
  } | null>(null);
  const queryHash = JSON.stringify(queryKey);

  if (preMountSnapshotRef.current?.queryHash !== queryHash) {
    preMountSnapshotRef.current = {
      queryHash,
      hasData: queryClient.getQueryData(queryKey) !== undefined,
      source: getPrV2CacheSource(queryKey),
    };
  }

  useEffect(() => {
    if (!queryEnabled) {
      return;
    }

    if (!preMountSnapshotRef.current?.hasData) {
      return;
    }

    logPrV2CacheUsage({
      target: 'code-changes',
      prKey: `${owner}/${repo}#${number}`,
      queryKey,
      source: preMountSnapshotRef.current.source ?? 'cache:unknown',
    });
  }, [number, owner, queryKey, repo]);

  return useQuery({
    ...codeChangesQuery,
    enabled: queryEnabled,
    refetchOnWindowFocus: false,
  });
}
